import { PageSizeEnum } from '../../../api/shared/enums/page-size.enum';
import { SortOrderEnum } from '../../../api/shared/enums/sort-order.enum';
import { MonitoringSortTypesEnum } from '../../../Common/MonitorShared/enum/monitoring-sort-types.enum';
import { SortFieldEnum } from '../../../Common/TargetingShared/enum/SortField.enum';
import { DateQueryModel } from '../../../Common/Forms/models/date-query.model';
import {
    currentUserAppPreferencesVersion,
    UserAppAccessibilityPreferences,
    UserAppGeneralPreferencesModel,
    UserAppMonitorPreferencesModel,
    UserAppPreferencesModel,
    UserAppReportPreferencesModel,
    UserAppSocialPreferencesModel,
    UserAppTargetingPreferencesModel,
    UserAppContactsPreferencesModel,
} from '../models/user-app-preferences.model';
import { UserAppPreferenceCommonFilters } from '../models/user-app-preferences-filters.model';
import { defaultChartColorsAgilitySession } from './default-chart-colors-agility-session';
import { MonitoringViewModesEnum } from '../../../Monitor/Shared/enums/monitoring-view-modes.enum';

/**
 * Changes in this file should usually be accompanied by a corresponding {@link UserAppPreferencesVersioner} change
 * Version changes should be encapsulated by a transformation function, which is then mapped to the previous version.
 */

const emptyCommonFilters: UserAppPreferenceCommonFilters = {
    name: [],
    createdBy: [],
    createdDate: DateQueryModel.getAllQuery(),
    modifiedBy: [],
    modifiedDate: DateQueryModel.getAllQuery(),
};

const userAppReportsPreferencesDefault: UserAppReportPreferencesModel = {
    defaultFilters: {
        ...emptyCommonFilters,
        visibility: null
    },
    defaultChartColors: defaultChartColorsAgilitySession
};

const userAppMonitoringPreferencesDefault: UserAppMonitorPreferencesModel = {
    topicsPrefs: {
        defaultFilters: {
            ...emptyCommonFilters,
            visibility: null,
            autoTag: [],
            autoSaving: null
        },
        defaultDisplaySettings: {
            showImages: false,
            showMentionIndex: false,
            isCompactMentionDisplay: false,
            sortType: MonitoringSortTypesEnum.published,
            sortDirection: SortOrderEnum.descending,
            pageSize: PageSizeEnum.twentyFive,
            resultsViewMode: MonitoringViewModesEnum.MENTION
        }
    },
    savedCoveragePrefs: {
        defaultFilters: {
            ...emptyCommonFilters,
            visibility: null
        },
        defaultDisplaySettings: {
            showImages: false,
            showMentionIndex: false,
            isCompactMentionDisplay: false,
            sortType: MonitoringSortTypesEnum.published,
            sortDirection: SortOrderEnum.descending,
            pageSize: PageSizeEnum.twentyFive,
            resultsViewMode: MonitoringViewModesEnum.MENTION
        }
    },
    briefingsPrefs: {
        defaultFilters: {
            ...emptyCommonFilters,
            visibility: null,
            status: [],
            deliveryMethod: []
        },
        defaultBriefingTemplate: 'BRIEFING_1',
        defaultDisplaySettings: {
            pageSize: PageSizeEnum.twentyFive
        }
    },
    coverageAlertsPrefs: {
        defaultFilters: {
            ...emptyCommonFilters,
            visibility: null,
            status: [],
            alertType: []
        }
    },
    subscriptionsPrefs: {
        defaultFilters: {
            ...emptyCommonFilters,
            visibility: null,
            status: [],
            type: []
        },
        defaultDisplaySettings: {
            resultsViewMode: MonitoringViewModesEnum.MENTION
        }
    },
    // we don't want to start with empty columns, we want to know when nothing has been saved yet (you can save empty)
    defaultExportColumns: null
};

const userAppTargetingPreferencesDefault: UserAppTargetingPreferencesModel = {
    savedSearchesPrefs: {
        defaultFilters: {
            ...emptyCommonFilters,
            type: []
        },
        defaultSearchSettings: {
            includePrivateContacts: true,
            locations: {
                includeAll: [],
                include: [],
                exclude: []
            },
            languages: {
                includeAll: [],
                include: [],
                exclude: []
            },
        },
        defaultDisplaySettings: {
            sortType: SortFieldEnum.CIRCULATION_UNIQUE_VISITORS,
            sortDirection: SortOrderEnum.descending,
            pageSize: PageSizeEnum.twentyFive
        }
    },
    targetedListsPrefs: {
        defaultFilters: {
            ...emptyCommonFilters,
            type: []
        },
        defaultDisplaySettings: {
            sortType: SortFieldEnum.CIRCULATION_UNIQUE_VISITORS,
            sortDirection: SortOrderEnum.descending,
            pageSize: PageSizeEnum.twentyFive
        }
    },
    distributionsPrefs: {
        defaultFilters: {
            ...emptyCommonFilters,
            status: []
        },
        defaultDistributionDetails: {
            fromName: '',
            replyTo: ''
        },
        defaultRecipients: [],
        sentDistributionDisplaySettings: {
            sortType: SortFieldEnum.CIRCULATION_UNIQUE_VISITORS,
            sortDirection: SortOrderEnum.descending,
            pageSize: PageSizeEnum.twentyFive
        },
        enableMppEstimates: false,
        defaultEmailAccount: 0
    }
};

const userAppSocialPreferencesDefault: UserAppSocialPreferencesModel = {
    // we don't want to start with empty columns, we want to know when nothing has been saved yet (you can save empty)
    defaultExportColumns: null
};

const userAppAccessibilityPrefsDefault: UserAppAccessibilityPreferences = {
    collapseToolbars: true,
    tooltipsOnFocus: false
};

const userAppContactsPreferencesDefault: UserAppContactsPreferencesModel = {
    defaultSelectedCheckboxes: []
}
const userAppGeneralPrefsDefault: UserAppGeneralPreferencesModel = {
    favoriteColors: [],
    accessibility: userAppAccessibilityPrefsDefault
};

export const userAppPreferencesDefault = {
    version: currentUserAppPreferencesVersion,
    monitoringPrefs: userAppMonitoringPreferencesDefault,
    targetingPrefs: userAppTargetingPreferencesDefault,
    reportsPrefs: userAppReportsPreferencesDefault,
    socialPrefs: userAppSocialPreferencesDefault,
    contactsPrefs: userAppContactsPreferencesDefault,
    generalPrefs: userAppGeneralPrefsDefault
} as UserAppPreferencesModel;
